import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiChevronDown, FiCheck, FiUsers, FiSettings } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import CreateTenantModal from '../modals/CreateTenantModal';

const TenantSwitcher = () => {
    const [showDropdown, setShowDropdown] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    // Utilise les données déjà présentes dans AuthContext (user.tenants est chargé par /auth/me)
    // Évite un double fetch /tenants/my indépendant depuis chaque instance de TenantSwitcher
    const { user, activeTenantId, switchTenant, refreshUser } = useAuth();
    const dropdownRef = useRef(null);
    const navigate = useNavigate();

    const tenants = user?.tenants || [];
    const activeTenant = tenants.find(t => t._id === activeTenantId || t.tenantId === activeTenantId) || null;

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };

        if (showDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showDropdown]);



    // Deletion of organizations is handled in TenantSettings (settings page)

    const getPlanBadge = (plan) => {
        const colors = {
            free: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
            starter: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
            team: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300',
            business: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
            enterprise: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
        };
        return plan === 'free'
            ? `${colors.free} border border-red-400` // highlight free plan
            : colors[plan] || colors.free;
    };

    const getRoleLabel = (role) => {
        const labels = {
            owner: 'Propriétaire',
            admin: 'Administrateur',
            member: 'Membre'
        };
        return labels[role] || role;
    };

    return (
        <>
            <div className="relative flex justify-center" ref={dropdownRef}>
                <button
                    onClick={() => setShowDropdown(!showDropdown)}
                    data-tutorial="tenant-switcher-btn"
                    className="flex items-center gap-3 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 transition min-w-[280px] w-full md:w-auto"
                >
                    {activeTenant && (
                        <div
                            className={`w-7 h-7 md:w-5 md:h-5 rounded-md flex items-center justify-center flex-shrink-0 font-semibold text-white text-sm ${
                                activeTenant.type === 'personal'
                                    ? 'bg-green-600'
                                    : 'bg-blue-600'
                            }`}
                        >
                            {activeTenant.name.charAt(0).toUpperCase()}
                        </div>
                    )}
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                        <span className="text-sm font-semibold text-black dark:text-white truncate">
                            {activeTenant?.name || 'Sélectionner une organisation'}
                        </span>
                        {activeTenant && (
                            <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                                · {getRoleLabel(activeTenant.userRole)}
                            </span>
                        )}
                    </div>  
                    <FiChevronDown className="h-4 w-4 text-gray-500 flex-shrink-0" />
                </button>

                {showDropdown && (
                    <div
                        className="fixed sm:absolute bottom-auto sm:top-full left-4 right-4 sm:left-0 sm:right-auto top-60 sm:bottom-auto mt-0 sm:mt-2 w-auto sm:w-96 bg-white dark:bg-gray-800 rounded-lg shadow-xl border dark:border-gray-700 py-2 z-[60]"
                    >
                        <div className="px-4 py-2 border-b dark:border-gray-700">
                            <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                                Mes organisations
                            </div>
                        </div>

                        <div className="overflow-y-auto max-h-[50vh] sm:max-h-56 custom-scrollbar">
                        {tenants.map((tenant, index) => (
                            <React.Fragment key={tenant._id}>
                                <button
                                    onClick={() => {
                                        switchTenant(tenant._id);
                                        setShowDropdown(false);
                                    }}
                                    className={`w-full text-left px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-3 transition ${
                                        tenant.isActive ? 'bg-blue-50 dark:bg-blue-900/10' : ''
                                    }`}
                                >
                                    <div
                                        className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 font-bold text-white shadow-sm ${
                                            tenant.type === 'personal'
                                                ? 'bg-gradient-to-br from-green-600 to-green-700'
                                                : 'bg-gradient-to-br from-blue-600 to-indigo-700'
                                        }`}
                                    >
                                        {tenant.name.charAt(0).toUpperCase()}
                                    </div>

                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-0.5">
                                            <span className="font-semibold text-black dark:text-white truncate text-sm">
                                                {tenant.name}
                                            </span>
                                            {tenant.isActive && (
                                                <FiCheck className="h-4 w-4 text-green-600 dark:text-green-500 flex-shrink-0" />
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                                            <span>{tenant.type === 'personal' ? 'Personnel' : 'Professionnel'}</span>
                                            <span>·</span>
                                            <span>{getRoleLabel(tenant.userRole)}</span>
                                        </div>
                                    </div>

                                    <span className={`text-xs px-2 py-1 rounded-md whitespace-nowrap font-semibold uppercase ${getPlanBadge(tenant.plan)} flex-shrink-0`}>
                                        {tenant.plan === 'free' ? 'Free' : tenant.plan}
                                    </span>
                                </button>
                                {/* n'affiche la bordure qu'entre les éléments, pas après le dernier */}
                                {index !== tenants.length - 1 && <div className="border-b dark:border-gray-700 mx-4"></div>}
                            </React.Fragment>
                        ))}
                        </div>

                        <div className="border-t dark:border-gray-700 pt-2">
                            {activeTenant && ['owner', 'admin'].includes(activeTenant.userRole) && (
                                <button
                                    onClick={() => {
                                        setShowDropdown(false);
                                        navigate('/tenant-settings');
                                    }}
                                    data-tutorial="org-settings-link"
                                    className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 text-gray-700 dark:text-gray-300 transition"
                                >
                                    <FiSettings className="h-4 w-4" />
                                    <span className="text-sm font-semibold">Gérer l'organisation</span>
                                </button>
                            )}
                            <button
                                onClick={() => {
                                    setShowDropdown(false);
                                    navigate('/manage-organizations');
                                }}
                                className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 text-gray-700 dark:text-gray-300 transition"
                            >
                                <FiUsers className="h-4 w-4" />
                                <span className="text-sm font-semibold">Gérer mes organisations</span>
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {showCreateModal && (
                <CreateTenantModal
                    onClose={() => setShowCreateModal(false)}
                    onSuccess={() => {
                        setShowCreateModal(false);
                        refreshUser();
                    }}
                />
            )}
        </>
    );
};

export default TenantSwitcher;